import { patientsApi } from '../patientApi';
import type { GetRefundWorkflowResponse, RefundWorkflowRequest } from './invoices.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasExactKeys = (value: Record<string, unknown>, required: readonly string[]): boolean => {
  const allowed = new Set(required);
  return (
    required.every((key) => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every((key) => allowed.has(key))
  );
};

export const parseRefundWorkflowResponse = (input: unknown): GetRefundWorkflowResponse => {
  if (
    !isRecord(input) ||
    !hasExactKeys(input, ['message', 'errorCode', 'data', 'info']) ||
    typeof input.message !== 'string' ||
    !Number.isSafeInteger(input.errorCode) ||
    !isRecord(input.info) ||
    !isRecord(input.data) ||
    !hasExactKeys(input.data, ['workflow']) ||
    (input.data.workflow !== 'enhanced' && input.data.workflow !== 'legacy')
  ) {
    throw new Error('Invalid refund wire response: workflow envelope shape');
  }

  return input as GetRefundWorkflowResponse;
};

const workflowParsingError = (originalStatus: number) => ({
  error: {
    data: 'Refund workflow response failed runtime validation',
    error: 'Invalid refund workflow wire response',
    originalStatus,
    status: 'PARSING_ERROR' as const
  }
});

export const refundWorkflowApi = patientsApi.injectEndpoints({
  endpoints: (builder) => ({
    getRefundWorkflow: builder.query<
      GetRefundWorkflowResponse['data'],
      RefundWorkflowRequest | undefined
    >({
      keepUnusedDataFor: 0,
      async queryFn(scope, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({ params: scope, url: '/refund-workflow' });
        if (result.error) return { error: result.error };

        try {
          return { data: parseRefundWorkflowResponse(result.data).data };
        } catch {
          return workflowParsingError(200);
        }
      }
    })
  })
});

export const { useLazyGetRefundWorkflowQuery } = refundWorkflowApi;
